import { TaskTypeEnum } from '@app/domain';
import { PaginatedResponse } from '@app/shared/models/global.model';
import {
  AlertAndNotification,
  AlertAndNotificationCreateResponse,
  AlertAndNotificationDetail,
  AlertAndNotificationListItem,
  AssetExpression,
  ChatNotification,
  CorrespondingItem,
  CorrespondingTask,
  EmailNotification,
  NonVesselSector,
  StandardNotification,
  SubTaskExpression,
  TaskExpression,
} from '@domain/entities/alerts-and-notifications.entity';
import {
  AlertAndNotificationCreateResponseDTO,
  AlertAndNotificationDetailDTO,
  AlertsAndNotificationsDTO,
  AssetExpressionDTO,
  ChatNotificationDTO,
  CorrespondingAssetDTO,
  CorrespondingTaskDTO,
  EmailNotificationDTO,
  NonVesselSectorDTO,
  StandardNotificationDTO,
  SubTaskExpressionDTO,
  TaskExpressionDTO,
} from '../dtos/alerts-and-notifications.dto';

// Maps between the API DTO and the Domain Entity
export class AlertsAndNotificationsMapper {
  alertsAndNotificationsToDomain(
    dto: AlertsAndNotificationsDTO[]
  ): AlertAndNotificationListItem[] {
    const [data] = dto;

    return data.data
      .filter(
        alertsAndNotification =>
          alertsAndNotification.name === TaskTypeEnum.APPROVAL ||
          alertsAndNotification.name === TaskTypeEnum.CHAT_NOTIFICATIONS ||
          alertsAndNotification.name === TaskTypeEnum.EMAIL_NOTIFICATIONS
      )
      .map(alertsAndNotification => {
        switch (alertsAndNotification.name) {
          case TaskTypeEnum.APPROVAL:
            return {
              name: TaskTypeEnum.APPROVAL,
              configs: alertsAndNotification.configs as AlertAndNotification[],
            };

          case TaskTypeEnum.CHAT_NOTIFICATIONS:
            return {
              name: TaskTypeEnum.CHAT_NOTIFICATIONS,
              configs: alertsAndNotification.configs as ChatNotification[],
            };

          case TaskTypeEnum.EMAIL_NOTIFICATIONS:
            return {
              name: TaskTypeEnum.EMAIL_NOTIFICATIONS,
              configs: alertsAndNotification.configs as EmailNotification[],
            };
        }
      });
  }

  alertAndNotificationDetailsToDomain(
    dto: AlertAndNotificationDetailDTO
  ): AlertAndNotificationDetail {
    return {
      id: dto.id,
      ruleName: dto.ruleName,
      taskConfig: dto.taskConfig,
      tasks: dto.tasks,
    };
  }

  createResponseToDomain(
    dto: AlertAndNotificationCreateResponseDTO
  ): AlertAndNotificationCreateResponse {
    return {
      id: dto.id,
    };
  }

  correspondingTasksToDomain(
    dto: PaginatedResponse<CorrespondingTaskDTO>
  ): PaginatedResponse<CorrespondingTask> {
    return {
      data: dto.data.map(dtoItem => ({
        id: dtoItem.id,
        taskCode: dtoItem.taskCode,
        taskName: dtoItem.taskName,
        isConfigured: dtoItem.isConfigured,
      })),
      page: dto.page,
      size: dto.size,
      totalElements: dto.totalElements,
      totalPages: dto.totalPages,
    };
  }

  expressionListToDomain(dtos: AssetExpressionDTO): AssetExpression {
    return {
      assetInfo: dtos.assetInfo,
      assetItemInfo: dtos.assetItemInfo.map(dto => ({
        id: dto.id,
        inputFieldName: dto.inputFieldName,
      })),
      collectAndReturnInfo: dtos.collectAndReturnInfo,
    };
  }

  emailNotificationToDomain(dto: EmailNotificationDTO): EmailNotification {
    const { subObjects } = dto.targetObject;
    return {
      id: dto.id,
      name: dto.name,
      trigger: dto.trigger,
      shiftType: dto.shiftType,
      notificationType: dto.notificationType,
      alertTo: dto.alertTo,
      alertType: dto.alertType,
      targetObject: {
        id: dto.targetObject.id,
        name: dto.targetObject.name,
        subObjects: subObjects
          ? subObjects.map(subObject => ({
              id: subObject.id,
              name: subObject.name,
            }))
          : null,
      },
      alertHeader: dto.alertHeader,
      alertContent: dto.alertContent,
    };
  }

  emailNotificationListToDomain(
    dtos: EmailNotificationDTO[]
  ): EmailNotification[] {
    return dtos.map(dto => this.emailNotificationToDomain(dto));
  }

  correspondingAssetToDomain(dto: CorrespondingAssetDTO): CorrespondingItem {
    return {
      id: dto.id,
      name: dto.assetName,
      isConfigured: dto.isConfigured,
    };
  }

  correspondingAssetsToDomain(
    dto: PaginatedResponse<CorrespondingAssetDTO>
  ): PaginatedResponse<CorrespondingItem> {
    return {
      data: dto.data.map(dtoItem => this.correspondingAssetToDomain(dtoItem)),
      page: dto.page,
      size: dto.size,
      totalElements: dto.totalElements,
      totalPages: dto.totalPages,
    };
  }

  taskExpressionToDomain(dto: TaskExpressionDTO): TaskExpression {
    const {
      TaskDetailDTO,
      TaskSubmitDTO,
      SubTaskDTO,
      inputFieldExpressions,
      optionalFieldExpressions,
      setExpressions,
      VesselByShiftEntity,
    } = dto;

    return {
      TaskDetailDTO: {
        taskName: TaskDetailDTO.taskName,
        taskShift: TaskDetailDTO.taskShift,
        taskType: TaskDetailDTO.taskType,
        taskDescription: TaskDetailDTO.taskDescription,
        canAsNa: TaskDetailDTO.canAsNa,
        isAdHoc: TaskDetailDTO.isAdHoc,
        isRepeatable: TaskDetailDTO.isRepeatable,
      },
      TaskSubmitDTO: {
        submittedBy: TaskSubmitDTO.submittedBy,
        completionTime: TaskSubmitDTO.completionTime,
        completionDate: TaskSubmitDTO.completionDate,
      },
      VesselByShiftEntity: {
        vesselName: VesselByShiftEntity?.vesselName,
        vvCode: VesselByShiftEntity?.vvCode,
        voyIn: VesselByShiftEntity?.voyIn,
        voyOut: VesselByShiftEntity?.voyOut,
        atb: VesselByShiftEntity?.atb,
        atu: VesselByShiftEntity?.atu,
      },
      SubTaskDTO: {
        subTaskDescription: SubTaskDTO.subTaskDescription,
        subTaskName: SubTaskDTO.subTaskName,
        attachments: SubTaskDTO.attachments,
        remarks: SubTaskDTO.remarks,
      },
      optionalFieldExpressions: optionalFieldExpressions.map(
        optionalFieldExpression =>
          this.subTaskExpressionToDomain(optionalFieldExpression)
      ),
      setExpressions: setExpressions.map(setExpression =>
        this.subTaskExpressionToDomain(setExpression)
      ),
      inputFieldExpressions: inputFieldExpressions.map(inputFieldExpression => {
        const [fieldNum] = Object.keys(inputFieldExpression);
        const inputFieldExpressionEntity: Record<string, SubTaskExpression[]> =
          {};

        inputFieldExpressionEntity[fieldNum] = inputFieldExpression[
          fieldNum
        ].map(inputField => this.subTaskExpressionToDomain(inputField));

        return inputFieldExpressionEntity;
      }),
    };
  }

  subTaskExpressionToDomain(dto: SubTaskExpressionDTO): SubTaskExpression {
    const { properties, setSetting, children } = dto;
    return {
      id: dto.id,
      index: dto.index,
      levelDepth: dto.levelDepth,
      properties: properties
        ? {
            dataSourceId: properties.dataSourceId,
            inputFieldName: properties.inputFieldName,
            inputFieldType: properties.inputFieldType,
            inputSubType: properties.inputSubType,
            isMandatory: properties.isMandatory,
          }
        : null,
      setSetting: setSetting
        ? {
            id: setSetting.id,
            fieldName: setSetting.fieldName,
            maxDuplicate: setSetting.maxDuplicate,
            minDuplicate: setSetting.minDuplicate,
            nameOfSet: setSetting.nameOfSet,
            submissionIds: setSetting.submissionIds,
            subTaskFormId: setSetting.subTaskFormId,
            dataSourceId: setSetting.dataSourceId,
          }
        : null,
      type: dto.type,
      children:
        children?.map(child => this.subTaskExpressionToDomain(child)) ?? [],
    };
  }

  standardNotificationToDomain(
    dto: StandardNotificationDTO
  ): StandardNotification {
    return {
      taskConfigType: dto.taskConfigType,
      emails: dto.emails,
      users: dto.users,
    };
  }

  standardNotificationListToDomain(
    dtos: StandardNotificationDTO[]
  ): StandardNotification[] {
    return dtos.map(dto => this.standardNotificationToDomain(dto));
  }

  chatNotificationToDomain(dto: ChatNotificationDTO): ChatNotification {
    const { subObjects } = dto.targetObject;
    return {
      id: dto.id,
      name: dto.name,
      trigger: dto.trigger,
      notificationType: dto.notificationType,
      publishToChat: {
        id: dto?.publishToChat?.id,
        name: dto?.publishToChat?.name,
      },
      targetObject: {
        id: dto.targetObject.id,
        name: dto.targetObject.name,
        subObjects:
          subObjects?.map(subObject => ({
            id: subObject.id,
            name: subObject.name,
          })) ?? null,
      },
      alertContent: dto.alertContent,
    };
  }

  chatNotificationListToDomain(
    dtos: ChatNotificationDTO[]
  ): ChatNotification[] {
    return dtos.map(dto => this.chatNotificationToDomain(dto));
  }

  nonVesselSectorListToDomain(dtos: NonVesselSectorDTO[]): NonVesselSector[] {
    return dtos.map(dto => this.nonVesselSectorToDomain(dto));
  }

  nonVesselSectorToDomain(dto: NonVesselSectorDTO): NonVesselSector {
    return {
      id: dto.id,
      sectorName: dto.sectorName,
    };
  }
}
